import type { PlatformSubpageConfig } from '../types';
import { defaultCtas, localImage } from './shared';

const img = (src: string, alt: string) => ({
  src: localImage(src),
  alt,
  width: 1200,
  height: 800,
});

export const firmwareSdkConfig: PlatformSubpageConfig = {
  slug: 'firmware-sdk',
  path: '/platform/firmware-sdk',
  breadcrumbLabel: 'Firmware SDK',
  hero: {
    title: 'Production-ready firmware SDKs for leading chipsets',
    subtitle:
      'ESP32, ESP8266, and more—with secure provisioning, device twins, and OTA support built in.',
    ...defaultCtas,
  },
  heroImage: img('/images/home/feature-firmware.jpg', 'Embedded firmware development'),
  eduIntro: {
    title: 'What is an IoT firmware SDK?',
    description:
      'A firmware SDK provides libraries, provisioning flows, and cloud connectivity so your embedded team ships connected products faster—without reinventing security, OTA, or state sync.',
  },
  advantages: {
    eyebrow: 'COMPETITIVE EDGE',
    title: 'Ship embedded firmware faster with proven SDKs',
    items: [
      { icon: 'cpu', title: 'Chipset-Ready Libraries', description: 'Pre-built SDKs for ESP32 and ESP8266 with documented integration paths.' },
      { icon: 'wifi', title: 'Flexible Provisioning', description: 'SmartConfig, AP mode, and factory credentials for smooth onboarding.' },
      { icon: 'refresh', title: 'Reliable OTA Updates', description: 'Staged rollouts with rollback and version channels for safer fleet updates.' },
    ],
  },
  featureShowcase: {
    eyebrow: 'SDK WORKFLOW',
    title: 'From silicon to cloud in a unified SDK',
    subtitle: 'Compare chipsets and see provisioning flows in action.',
    items: [
      {
        title: 'SmartConfig provisioning',
        description: 'Zero-touch Wi-Fi setup for consumer products with fallback AP mode when needed.',
        image: img('/images/home/feature-firmware.jpg', 'Provisioning'),
        cta: { label: 'View provisioning', to: '/contact-us' },
      },
      {
        title: 'Secure device authentication',
        description: 'Mutual TLS, token-based auth, and credential storage designed for production fleets.',
        image: img('/images/home/feature-cloud.jpg', 'Security'),
        cta: { label: 'Learn security', to: '/contact-us' },
      },
      {
        title: 'OTA firmware updates',
        description: 'Delta and full-image updates with progress reporting and automatic rollback on failure.',
        image: img('/images/home/ota_updates_android.png', 'OTA updates'),
        cta: { label: 'Explore OTA', to: '/contact-us' },
      },
    ],
  },
  capabilities: {
    eyebrow: 'CAPABILITIES',
    title: 'Embedded platform features out of the box',
    items: [
      { icon: 'wifi', title: 'SmartConfig Provisioning', description: 'Zero-touch Wi-Fi setup for consumer products.' },
      { icon: 'layers', title: 'AP Mode Setup', description: 'Fallback provisioning when SmartConfig is unavailable.' },
      { icon: 'refresh', title: 'OTA Updates', description: 'Delta and full-image updates with progress reporting.' },
      { icon: 'key', title: 'Device Pairing', description: 'Secure binding between devices, homes, and accounts.' },
      { icon: 'cloud', title: 'Offline State Handling', description: 'Queue and reconcile state when connectivity returns.' },
      { icon: 'shield', title: 'Secure Authentication', description: 'Mutual TLS and token-based device auth.' },
    ],
  },
  ctaBanner: {
    title: 'Accelerate your embedded roadmap with our firmware SDK',
    ctaLabel: 'Get Started',
    ctaTo: '/contact-us',
    imageSrc: '/images/cta/solution-cta-robot.webp',
  },
  faqs: {
    title: 'Frequently asked questions',
    items: [
      { question: 'Which chipsets are supported?', answer: 'ESP32, ESP8266, and additional chipsets via partner SDK paths.' },
      { question: 'Does the SDK include OTA?', answer: 'Yes—staged OTA with rollback, channels, and progress reporting.' },
      { question: 'How is provisioning handled?', answer: 'SmartConfig, AP mode, and factory credential flows are included.' },
    ],
  },
  sections: {
    eyebrow: 'ARCHITECTURE',
    title: 'From silicon to cloud in a unified SDK',
    subtitle: 'Compare chipsets and see provisioning flows in action.',
  },
};

export const firmwareChips = {
  esp32: {
    label: 'ESP32',
    features: ['Wi-Fi + BLE', 'Secure boot', 'Rich peripheral support', 'Low-power modes'],
    code: `#include "aeviot_sdk.h"

void setup() {
  Aeviot.begin();
  Aeviot.provisionSmartConfig();
}

void loop() {
  Aeviot.mqttLoop();
  Aeviot.syncTwin();
}`,
  },
  esp8266: {
    label: 'ESP8266',
    features: ['Cost-optimized Wi-Fi', 'OTA ready', 'AP mode setup', 'Lightweight footprint'],
    code: `#include "aeviot_sdk.h"

void setup() {
  Aeviot.begin(WIFI_SSID, WIFI_PASS);
  Aeviot.registerDevice();
}

void loop() {
  Aeviot.publishState();
}`,
  },
} as const;
